import { Close } from '@mui/icons-material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { FC, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { SerializableAppointmentEntry } from '../../models/SerializableSdkTypes/SerializableAppointmentEntry'
import {
  useArchiveAppointmentMutation,
  useGetServicesQuery,
  useHandleAppointmentScheduleRequestResponseMutation
} from '../../redux/api'
import { useGetCannedJobsQuery } from '../../redux/api/cannedJobApi'
import { CustomerEntry, RepairOrderExpanded, VehicleEntry } from '../../sdk'
import { describeVehicle } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { localDateTimeArrayToDate } from '../../utils/localDateTimeArraytoDate/localDateTimeArrayToDate'
import { CenteredLoading } from '../CenteredLoading/CenteredLoading'
import { CardUI } from '../SharedUI'
import { ProposeNewDayTimeDialog } from './ProposeNewDayTimeDialog'

interface Props {
  appointment: SerializableAppointmentEntry
  vehicle: VehicleEntry | undefined
  customer: CustomerEntry
  repairOrder: RepairOrderExpanded | undefined
  setEditing: (editing: boolean) => void
  cancelCallBack?: () => void
  deleteCallBack?: () => void
}

export const AppointmentPreview: FC<Props> = ({
  appointment,
  vehicle,
  customer,
  repairOrder,
  setEditing,
  cancelCallBack,
  deleteCallBack
}) => {
  const navigate = useNavigate()

  const { accessToken, shopId } = useAccessTokenAndShopId()

  const [archiveAppointment] = useArchiveAppointmentMutation()
  const [handleAppointmentScheduleRequestResponse] =
    useHandleAppointmentScheduleRequestResponseMutation()

  const { data: serviceEntries, isLoading: loadingServiceEntries } =
    useGetServicesQuery(
      {
        authorization: accessToken,
        shop: shopId!,
        services: appointment.info.services!
      },
      {
        skip:
          appointment.info.services === undefined ||
          appointment.info.services.length === 0
      }
    )

  const { data: cannedJobEntries, isLoading: loadingCannedJobEntries } =
    useGetCannedJobsQuery(
      {
        authorization: accessToken,
        shop: shopId!,
        cannedJobs: appointment.info.cannedJobs!
      },
      {
        skip:
          appointment.info.cannedJobs === undefined ||
          appointment.info.cannedJobs.length === 0
      }
    )

  const isLoading = loadingServiceEntries || loadingCannedJobEntries

  const [openDelete, setOpenDelete] = useState(false)

  const customerName = useMemo(() => {
    return formatName(customer.information)
  }, [customer])

  const Heading = useMemo(
    () => (
      <Stack spacing={1} data-testid="appointmentPreview">
        <Typography variant="h2">{customerName}</Typography>
        <Stack direction="row" spacing={2}>
          <Typography variant="body2">
            {dayjs(
              localDateTimeArrayToDate(appointment!.info!.startDateTime!)
            ).format('dddd, MMM D')}
          </Typography>
          <Typography variant="body2">
            {dayjs(
              localDateTimeArrayToDate(appointment!.info!.startDateTime!)
            ).format('hh:mmA')}
            -
            {dayjs(localDateTimeArrayToDate(appointment.info!.startDateTime!))
              .add(appointment.info!.estimatedWorkHours!, 'hour')
              .format('hh:mmA')}
          </Typography>
        </Stack>
      </Stack>
    ),
    [appointment, customerName]
  )

  const newEstimateUrl = useMemo(
    () =>
      appointment.info!.vehicle
        ? AppRoutes.NEW_ESTIMATE +
          '?customerId=' +
          appointment.info!.customer! +
          '&vehicleId=' +
          appointment.info!.vehicle +
          '&appointmentId=' +
          appointment.id!
        : AppRoutes.NEW_ESTIMATE +
          '?customerId=' +
          appointment.info!.customer! +
          '&appointmentId=' +
          appointment.id!,
    [appointment.id, appointment.info]
  )
  const exEstimateUrl = useMemo(
    () =>
      repairOrder ? AppRoutes.ESTIMATES + '/' + repairOrder.entry!.id! : '',
    [repairOrder]
  )

  const handleDelete = useCallback(async () => {
    setOpenDelete(false)
    await archiveAppointment({
      authorization: accessToken,
      shop: shopId,
      appointment: appointment.id!
    })
    if (deleteCallBack) {
      deleteCallBack()
    }
  }, [accessToken, appointment.id, archiveAppointment, deleteCallBack, shopId])

  const ActionsRight = useMemo(
    () => (
      <Stack direction="row" spacing={1}>
        <IconButton
          size="small"
          onClick={() => {
            setOpenDelete(true)
          }}
          aria-label="deleteIcon">
          <DeleteOutlineIcon />
        </IconButton>
        {cancelCallBack && (
          <IconButton
            size="small"
            onClick={() => {
              cancelCallBack()
            }}>
            <Close />
          </IconButton>
        )}
      </Stack>
    ),
    [cancelCallBack]
  )

  const confirmedAppointment = useMemo(() => {
    return (
      <CardUI
        heading={Heading}
        actionsRight={ActionsRight}
        aria-label="appointmentPreview">
        <Stack spacing={1}>
          {vehicle && (
            <Typography variant="body1">
              {describeVehicle(vehicle.info!)}
            </Typography>
          )}
          <Typography variant="body1">
            Concern: {appointment.info!.concern}
          </Typography>
          <Typography variant="body1">
            Notes: {appointment.info!.notes}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setEditing(true)
            }}>
            Edit
          </Button>
          {!repairOrder && (
            <Button onClick={() => navigate(newEstimateUrl)} variant="outlined">
              Create Estimate
            </Button>
          )}
          {repairOrder !== undefined && repairOrder.entry!.info!.isEstimate && (
            <Button onClick={() => navigate(exEstimateUrl)} variant="outlined">
              Go to Estimate
            </Button>
          )}
          {repairOrder !== undefined &&
            !repairOrder.entry!.info!.isEstimate && (
              <Button
                onClick={() => navigate(exEstimateUrl)}
                variant="outlined">
                Go to Repair Order
              </Button>
            )}
        </Stack>
      </CardUI>
    )
  }, [
    ActionsRight,
    Heading,
    appointment.info,
    exEstimateUrl,
    navigate,
    newEstimateUrl,
    repairOrder,
    setEditing,
    vehicle
  ])

  const handleRequest = useCallback(async () => {
    await handleAppointmentScheduleRequestResponse({
      authorization: accessToken,
      appointmentScheduleResponseRequest: {
        appointmentId: appointment.id,
        shopId,
        isRequestAccepted: true,
        sendEmail: !!customer.information?.email,
        sendSms: !!customer.information?.phone,
        newDateTime: undefined,
        title: undefined,
        message: undefined
      }
    })

    if (cancelCallBack) {
      cancelCallBack()
    }
  }, [
    accessToken,
    appointment.id,
    cancelCallBack,
    customer.information?.email,
    customer.information?.phone,
    handleAppointmentScheduleRequestResponse,
    shopId
  ])

  const [proposeNewDayTime, setProposeNewDayTime] = useState<boolean>(false)

  const scheduledAppointment = useMemo(() => {
    return (
      <>
        {proposeNewDayTime && (
          <ProposeNewDayTimeDialog
            open={proposeNewDayTime}
            handleCancel={() => setProposeNewDayTime(false)}
            appointment={appointment}
            customer={customer}
            vehicle={vehicle!}
          />
        )}
        <CardUI heading={Heading} actionsRight={ActionsRight}>
          {isLoading ? (
            <CenteredLoading />
          ) : (
            <Stack spacing={1}>
              {vehicle && (
                <Typography variant="body1">
                  {describeVehicle(vehicle.info!)}
                </Typography>
              )}

              <Typography>Services pre-selected:</Typography>

              {appointment.info.services!.length === 0 &&
              appointment.info.cannedJobs!.length === 0 ? (
                <List
                  style={{
                    listStyleType: 'disc',
                    marginLeft: '50px',
                    padding: '0'
                  }}>
                  <ListItem style={{ display: 'list-item', padding: '0' }}>
                    <ListItemText primary={'No services selected'} />
                  </ListItem>
                </List>
              ) : serviceEntries === undefined &&
                cannedJobEntries === undefined ? (
                <Alert color="error">Error loading service options</Alert>
              ) : (
                <List
                  style={{
                    listStyleType: 'disc',
                    marginLeft: '50px',
                    padding: '0'
                  }}>
                  {serviceEntries?.map((entry, index) => (
                    <ListItem
                      key={index}
                      style={{ display: 'list-item', padding: '0' }}>
                      <ListItemText primary={entry.info?.title} />
                    </ListItem>
                  ))}
                  {cannedJobEntries?.map((entry, index) => (
                    <ListItem
                      key={index}
                      style={{ display: 'list-item', padding: '0' }}>
                      <ListItemText primary={entry.info?.name} />
                    </ListItem>
                  ))}
                </List>
              )}

              <Typography variant="body1">
                Concern: {appointment.info!.concern}
              </Typography>

              <Button
                variant="contained"
                onClick={() => {
                  handleRequest()
                }}>
                Confirm Appointment
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setProposeNewDayTime(true)
                }}>
                Propose new Day/Time
              </Button>
            </Stack>
          )}
        </CardUI>
      </>
    )
  }, [
    ActionsRight,
    Heading,
    appointment,
    cannedJobEntries,
    customer,
    handleRequest,
    isLoading,
    proposeNewDayTime,
    serviceEntries,
    vehicle
  ])

  return (
    <>
      <Dialog open={openDelete}>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>Do you want to cancel this appointment?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="warning">
            Delete Appointment
          </Button>
        </DialogActions>
      </Dialog>
      {appointment.info.isConfirmed
        ? confirmedAppointment
        : scheduledAppointment}
    </>
  )
}
